import React, { useState, useEffect, useCallback, useRef } from 'react'
import { Container, Row, Col, Card, Table, Badge, Button, Alert, Spinner, Modal } from '@themesberg/react-bootstrap'
import { useCookies } from 'react-cookie'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlay, faPause, faSync, faEye, faUserPlus, faTrash, faBroom, faPrint, faSearch, faTimes } from '@fortawesome/free-solid-svg-icons'
import moment from 'moment'
import { getPrintQueueList, manualAssignPrintQueue, cleanupCompletedPrintQueue } from '../../../controller/api/printQueue'
import { getEmployeesForAssign, getAllDevices } from '../../../controller/api/device'
import { getAllGames } from '../../../controller/api/game'
import { cancelCart } from '../../../controller/api/cart'
import { getTicketsByCartId } from '../../../controller/api/__classic__/ticket'
import { deleteTicket } from '../../../controller/api/ticket'
import { cancelLotoTicket } from '../../../controller/api/__loto__/ticket'
import { FULL_LIST_ITEM_PER_PAGE, getItemPerPage } from '../../../helper/helper'
import { Routes } from '../../../routes'
import './PrintQueueMonitor.css'

import LogoKeno from '../../../assets/img/logo-keno.png'
import LogoMega from '../../../assets/img/logo-mega.png'
import LogoPower from '../../../assets/img/logo-power.png'
import LogoMax3d from '../../../assets/img/logo-max3d.png'
import LogoMax3dPro from '../../../assets/img/logo-max3dpro.png'

const GAME_CONFIG = {
  keno: { logo: LogoKeno, name: 'KENO' },
  mega: { logo: LogoMega, name: 'MEGA' },
  power: { logo: LogoPower, name: 'POWER' },
  max3d: { logo: LogoMax3d, name: 'MAX3D' },
  max3dpro: { logo: LogoMax3dPro, name: 'MAX3D PRO' }
}

const PrintQueueMonitor = () => {
  const [cookies] = useCookies(['lottmekong'])
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  // Filters
  const [filterStatus, setFilterStatus] = useState('') // '', 0, 1, 2, 3, -1
  const [filterType, setFilterType] = useState('All') // All | Vietlott | Classic | Loto
  const [filterGameId, setFilterGameId] = useState('')
  const [filterDeviceId, setFilterDeviceId] = useState('')
  const [filterTicketId, setFilterTicketId] = useState('')
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [lastUpdate, setLastUpdate] = useState(null)
  const [error, setError] = useState(null)
  const [cleaning, setCleaning] = useState(false)
  const [cleanupMessage, setCleanupMessage] = useState('')
  const [cleanupError, setCleanupError] = useState('')
  const [stats, setStats] = useState({
    total: 0,
    totalVietlott: 0,
    totalClassic: 0,
    totalLoto: 0
  })
  // Pagination
  const [currentPage, setCurrentPage] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [totalItems, setTotalItems] = useState(0)
  const [showDetailModal, setShowDetailModal] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [showAssignModal, setShowAssignModal] = useState(false)
  const [assignTarget, setAssignTarget] = useState(null)
  const [employees, setEmployees] = useState([])
  const [empLoading, setEmpLoading] = useState(false)
  const [empSearch, setEmpSearch] = useState('')
  const [selectedEmployee, setSelectedEmployee] = useState(null)
  const [assigning, setAssigning] = useState(false)
  const [assignError, setAssignError] = useState('')
  const [assignSuccess, setAssignSuccess] = useState('')
  // Device filter state
  const [devices, setDevices] = useState([])
  const [devicesLoading, setDevicesLoading] = useState(false)
  const [devicesLoaded, setDevicesLoaded] = useState(false)
  // Game filter state
  const [games, setGames] = useState([])
  const [gamesLoading, setGamesLoading] = useState(false)
  const [gamesLoaded, setGamesLoaded] = useState(false)
  // Bulk assign state
  const [selectedItems, setSelectedItems] = useState(new Set())
  const [showBulkAssignModal, setShowBulkAssignModal] = useState(false)
  const [bulkAssigning, setBulkAssigning] = useState(false)
  const [bulkAssignError, setBulkAssignError] = useState('')
  const [bulkAssignSuccess, setBulkAssignSuccess] = useState('')
  const [bulkAssignProgress, setBulkAssignProgress] = useState({ current: 0, total: 0 })
  // Cancel cart state
  const [cancelling, setCancelling] = useState(null)

  const intervalRef = useRef(null)

  // Function to fetch devices for dropdown
  const fetchDevices = useCallback(async () => {
    if (devicesLoaded) return // Only load once
    
    try {
      setDevicesLoading(true)
      const response = await getAllDevices(cookies.lottmekong, 0, FULL_LIST_ITEM_PER_PAGE)
      
      if (response && response.data) {
        // Create a formatted list for dropdown with "All Devices" option
        const deviceOptions = [{ id: '', code: 'Tất cả thiết bị' }, ...response.data]
        setDevices(deviceOptions)
        setDevicesLoaded(true)
      }
    } catch (err) {
      console.error('Error fetching devices:', err)
      setDevices([{ id: '', code: 'Tất cả thiết bị' }])
    } finally {
      setDevicesLoading(false)
    }
  }, [cookies.lottmekong, devicesLoaded])

  // Function to fetch games for dropdown
  const fetchGames = useCallback(async () => {
    if (gamesLoaded) return // Only load once
    
    try {
      setGamesLoading(true)
      const response = await getAllGames(cookies.lottmekong)
      
      if (response && response.data) {
        // Create a formatted list for dropdown with "All Games" option
        const gameOptions = [{ id: '', name: 'Tất cả game' }, ...response.data]
        setGames(gameOptions)
        setGamesLoaded(true)
      }
    } catch (err) {
      console.error('Error fetching games:', err)
      setGames([{ id: '', name: 'Tất cả game' }])
    } finally {
      setGamesLoading(false)
    }
  }, [cookies.lottmekong, gamesLoaded])

  const fetchData = useCallback(async (showLoading = true, page = currentPage) => {
    try {
      if (showLoading) setLoading(true)
      setError(null)
      const query = {
        type: filterType,
        status: filterStatus === '' ? undefined : Number(filterStatus),
        gameId: filterGameId || undefined,
        deviceId: filterDeviceId || undefined,
        ticketId: filterTicketId || undefined,
      }
      const response = await getPrintQueueList(cookies.lottmekong, query, page)
      
      if (response) {
        // Show all items including cancelled ones (status -1 or 3)
        const allItems = response.data || []
        // Sort data by createdAt descending (newest first)
        const sortedData = allItems.sort((a, b) => {
          const dateA = new Date(a.createdAt || 0)
          const dateB = new Date(b.createdAt || 0)
          return dateB - dateA
        })
        setData(sortedData)
        
        // Update pagination info - backend now returns correct total (already filtered)
        const total = response.total || 0
        setTotalItems(total)
        const pages = Math.max(1, Math.ceil(total / getItemPerPage()))
        setTotalPages(pages)
        
        console.log('Pagination Debug:', { 
          total, 
          itemPerPage: getItemPerPage(),
          pages, 
          currentPage: page,
          dataLength: sortedData.length
        })
        
        // Recompute stats from response totals
        setStats({
          total: total,
          totalVietlott: response.totalVietlott || 0,
          totalClassic: response.totalClassic || 0,
          totalLoto: response.totalLoto || 0
        })
        setLastUpdate(new Date())
      }
    } catch (err) {
      console.error('Error fetching print queue data:', err)
      setError('Không thể tải dữ liệu. Vui lòng thử lại.')
    } finally {
      if (showLoading) setLoading(false)
    }
  }, [cookies.lottmekong, filterType, filterStatus, filterGameId, filterDeviceId, filterTicketId, currentPage])

  // Auto refresh effect
  useEffect(() => {
    if (autoRefresh) {
      // Initial load
      fetchData()
      
      // Set up interval for auto-refresh every 5 seconds
      intervalRef.current = setInterval(() => {
        fetchData(false) // Don't show loading for auto-refresh
      }, 5000)
    } else {
      // Clear interval when auto-refresh is disabled
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
        intervalRef.current = null
      }
    }

    // Cleanup interval on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current)
      }
    }
  }, [autoRefresh, fetchData])

  // Load devices and games on component mount
  useEffect(() => {
    fetchDevices()
    fetchGames()
  }, [fetchDevices, fetchGames])

  // Reset to page 0 when filters change
  useEffect(() => {
    setCurrentPage(0)
  }, [filterStatus, filterType, filterGameId, filterDeviceId, filterTicketId])

  const toggleAutoRefresh = () => {
    setAutoRefresh(!autoRefresh)
  }

  const handleManualRefresh = () => {
    fetchData(true, currentPage)
  }

  const handleCleanup = async () => {
    const confirmed = window.confirm(
      'Quét các vé đã hoàn thành nhưng vẫn còn hiển thị “Đã in” trong hàng đợi?\n\nDữ liệu lịch sử không bị xóa; hệ thống chỉ đồng bộ trạng thái sang “Hoàn thành”.'
    )
    if (!confirmed) return

    setCleaning(true)
    setCleanupMessage('')
    setCleanupError('')
    try {
      const result = await cleanupCompletedPrintQueue(cookies.lottmekong)
      if (result?.status !== 'success') {
        throw new Error(result?.message || 'Không thể quét dọn hàng đợi')
      }

      const cleanedCount = Number(result.cleanedCount || 0)
      setCleanupMessage(
        cleanedCount > 0
          ? `Đã dọn ${cleanedCount} bản ghi không còn thuộc hàng đợi.`
          : 'Hàng đợi sạch, không có bản ghi rác.'
      )
      await fetchData(false, currentPage)
    } catch (err) {
      console.error('Error cleaning print queue:', err)
      setCleanupError(err?.response?.data?.message || err?.message || 'Không thể quét dọn hàng đợi')
    } finally {
      setCleaning(false)
    }
  }

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages) {
      setCurrentPage(newPage)
      fetchData(true, newPage)
    }
  }

  const handleShowDetail = (item) => {
    setSelectedItem(item)
    setShowDetailModal(true)
  }

  const handleCloseDetail = () => {
    setShowDetailModal(false)
    setSelectedItem(null)
  }

  const openAssignModal = (item) => {
    setAssignTarget(item)
    setShowAssignModal(true)
    setSelectedEmployee(null)
    setAssignError('')
    setAssignSuccess('')
    loadEmployees('')
  }

  const closeAssignModal = () => {
    setShowAssignModal(false)
    setAssignTarget(null)
    setEmployees([])
    setEmpSearch('')
    setSelectedEmployee(null)
    setAssignError('')
    setAssignSuccess('')
  }

  const loadEmployees = useCallback(async (searchTerm = '') => {
    try {
      setEmpLoading(true)
      const result = await getEmployeesForAssign(cookies.lottmekong, searchTerm)
      if (result && result.status === 'success') {
        setEmployees(result.data || [])
      } else {
        setEmployees([])
      }
    } finally {
      setEmpLoading(false)
    }
  }, [cookies.lottmekong])

  useEffect(() => {
    if (showAssignModal) {
      loadEmployees(empSearch)
    }
  }, [showAssignModal, empSearch, loadEmployees])

  const handleAssign = async () => {
    if (!assignTarget) return
    setAssignError('')
    setAssignSuccess('')
    setAssigning(true)
    try {
      const payload = {
        type: assignTarget.type,
        id: assignTarget.id,
        employeeId: selectedEmployee ? selectedEmployee.id : null
      }
      const result = await manualAssignPrintQueue(cookies.lottmekong, payload)
      if (result && result.status === 'success') {
        setAssignSuccess('Gán thành công')
        // Refresh list and close
        await fetchData(false)
        setTimeout(() => {
          closeAssignModal()
        }, 800)
      } else {
        setAssignError(result?.message || 'Không thể gán')
      }
    } catch (e) {
      setAssignError('Lỗi khi gọi API')
    } finally {
      setAssigning(false)
    }
  }

  // Bulk assign functions
  const getItemKey = (item) => `${item.type}-${item.id}`

  const handleCheckboxChange = (item) => {
    const key = getItemKey(item)
    setSelectedItems(prev => {
      const newSet = new Set(prev)
      if (newSet.has(key)) {
        newSet.delete(key)
      } else {
        newSet.add(key)
      }
      return newSet
    })
  }

  const handleSelectAll = () => {
    if (selectedItems.size === data.length) {
      // Unselect all
      setSelectedItems(new Set())
    } else {
      // Select all items on current page
      const newSet = new Set()
      data.forEach(item => {
        newSet.add(getItemKey(item))
      })
      setSelectedItems(newSet)
    }
  }

  const getSelectedItemsData = () => {
    return data.filter(item => selectedItems.has(getItemKey(item)))
  }

  const openBulkAssignModal = () => {
    if (selectedItems.size === 0) return
    setShowBulkAssignModal(true)
    setSelectedEmployee(null)
    setBulkAssignError('')
    setBulkAssignSuccess('')
    loadEmployees('')
  }

  const closeBulkAssignModal = () => {
    setShowBulkAssignModal(false)
    setEmployees([])
    setEmpSearch('')
    setSelectedEmployee(null)
    setBulkAssignError('')
    setBulkAssignSuccess('')
    setBulkAssignProgress({ current: 0, total: 0 })
  }

  const handleBulkAssign = async () => {
    if (!selectedEmployee || selectedItems.size === 0) return
    
    setBulkAssignError('')
    setBulkAssignSuccess('')
    setBulkAssigning(true)
    
    const itemsToAssign = getSelectedItemsData()
    let successCount = 0
    let failCount = 0
    
    setBulkAssignProgress({ current: 0, total: itemsToAssign.length })
    
    for (let i = 0; i < itemsToAssign.length; i++) {
      const item = itemsToAssign[i]
      try {
        const payload = {
          type: item.type,
          id: item.id,
          employeeId: selectedEmployee.id
        }
        const result = await manualAssignPrintQueue(cookies.lottmekong, payload)
        if (result && result.status === 'success') {
          successCount++
        } else {
          failCount++
        }
      } catch (e) {
        failCount++
      }
      setBulkAssignProgress({ current: i + 1, total: itemsToAssign.length })
    }
    
    // Show result
    if (failCount === 0) {
      setBulkAssignSuccess(`Đã gán thành công ${successCount}/${itemsToAssign.length} vé`)
    } else {
      setBulkAssignSuccess(`Gán thành công ${successCount} vé, thất bại ${failCount} vé`)
    }
    
    // Clear selection and refresh
    setSelectedItems(new Set())
    await fetchData(false)
    
    setTimeout(() => {
      closeBulkAssignModal()
    }, 1500)
    
    setBulkAssigning(false)
  }

  const handleCancelCart = async (item) => {
    try {
      // Determine what type of item we're canceling
      const itemType = item.type
      const itemId = item.id
      
      let confirmMessage = ''
      
      if (itemType === 'Classic') {
        confirmMessage = `Bạn có chắc chắn muốn hủy đơn hàng Classic (ID: ${itemId})?\n\nThao tác này sẽ:\n- Hủy tất cả các vé trong đơn hàng\n- Hoàn tiền về tài khoản người dùng\n- Không thể khôi phục`
      } else if (itemType === 'Vietlott') {
        confirmMessage = `Bạn có chắc chắn muốn hủy vé Vietlott (ID: ${itemId})?\n\nThao tác này sẽ:\n- Hủy vé này\n- Hoàn tiền về tài khoản người dùng\n- Không thể khôi phục`
      } else if (itemType === 'Loto') {
        confirmMessage = `Bạn có chắc chắn muốn hủy vé Loto (ID: ${itemId})?\n\nThao tác này sẽ:\n- Hủy vé này\n- Hoàn tiền về tài khoản người dùng\n- Không thể khôi phục`
      }
      
      if (!window.confirm(confirmMessage)) {
        return
      }

      setCancelling(itemId)
      
      let result = null
      
      if (itemType === 'Classic') {
        // For Classic type, need to fetch tickets first then cancel the cart
        const ticketsResponse = await getTicketsByCartId(cookies.lottmekong, itemId)
        
        if (!ticketsResponse || !ticketsResponse.data) {
          alert('Không thể lấy thông tin vé. Vui lòng thử lại.')
          return
        }
        
        const tickets = ticketsResponse.data
        result = await cancelCart(cookies.lottmekong, itemId, tickets)
      } else if (itemType === 'Loto') {
        const cancelResponse = await cancelLotoTicket(cookies.lottmekong, itemId)
        result = {
          success: cancelResponse?.status === 'success',
          message: cancelResponse?.message || 'Có lỗi xảy ra khi hủy vé Loto'
        }
      } else {
        // Vietlott dùng luồng huỷ vé thường.
        const deleteResponse = await deleteTicket(cookies.lottmekong, itemId)
        
        if (deleteResponse) {
          result = {
            success: true,
            message: 'Đã hủy vé thành công'
          }
        } else {
          result = {
            success: false,
            message: 'Có lỗi xảy ra khi hủy vé'
          }
        }
      }
      
      if (result && result.success) {
        alert(result.message)
        
        // Immediately remove the cancelled item from local state
        setData(prevData => {
          const filtered = prevData.filter(dataItem => {
            // Compare both string and number formats
            return String(dataItem.id) !== String(itemId)
          })
          console.log('Removed item:', itemId, 'Remaining items:', filtered.length)
          return filtered
        })
        
        // Update total items count
        setTotalItems(prev => Math.max(0, prev - 1))
        
        // Wait a bit for backend to process and then refresh
        await new Promise(resolve => setTimeout(resolve, 1000))
        // Refresh the list with current page
        await fetchData(true, currentPage)
      } else {
        alert(result?.message || 'Có lỗi xảy ra khi hủy')
      }
    } catch (error) {
      console.error('Error canceling:', error)
      alert('Có lỗi xảy ra khi hủy')
    } finally {
      setCancelling(null)
    }
  }

  const getPeriodName = (periodId) => {
    if (!periodId) return null
    const idx = periodId.indexOf('-')
    return idx >= 0 && idx + 1 < periodId.length ? periodId.substring(idx + 1) : periodId
  }

  const getStatusBadge = (status) => {
    switch (status) {
      case -1:
        return <Badge bg="light" text="danger" className="border">Đã hủy</Badge>
      case 0:
        return <Badge bg="warning" text="dark">Chờ in</Badge>
      case 1:
        return <Badge bg="primary">Đang in</Badge>
      case 2:
        return <Badge bg="light" text="success" className="border">Đã in</Badge>
      case 3:
        return <Badge bg="light" text="success" className="border">Hoàn thành</Badge>
      default:
        return <Badge bg="secondary">Không xác định</Badge>
    }
  }

  const getTypeBadge = (type) => {
    return <Badge bg="light" text="dark" className="border fw-normal">{type}</Badge>
  }

  const getGameConfig = (gameId) => {
    if (!gameId) return null
    return GAME_CONFIG[gameId.toLowerCase()] || null
  }

  const getGameLogo = (gameId) => {
    const config = getGameConfig(gameId)
    if (!config) return null
    return (
      <div className="d-flex align-items-center">
        <img 
          src={config.logo} 
          alt={config.name} 
          style={{ 
            width: '24px', 
            height: '24px', 
            marginRight: '6px',
            borderRadius: '4px'
          }} 
        />
        <span className="fw-semibold text-body">{config.name}</span>
      </div>
    )
  }

  const getRowStyle = (gameId) => {
    return {}
  }

  const renderTicketIdLink = (item) => (
    <a
      href={`${Routes.VietlottTicket.path}?ticketId=${encodeURIComponent(item.id)}`}
      target="_blank"
      rel="noopener noreferrer"
      title="Mở trang tra cứu vé"
    >
      <code className="small">{item.id}</code>
    </a>
  )

  const formatDateTime = (dateString) => {
    if (!dateString) return '-'
    try {
      return moment(dateString).format('DD/MM/YYYY HH:mm:ss')
    } catch {
      return '-'
    }
  }

  const activeFilterCount = [
    filterStatus !== '',
    filterType !== 'All',
    Boolean(filterGameId),
    Boolean(filterDeviceId),
    Boolean(filterTicketId)
  ].filter(Boolean).length

  const clearFilters = () => {
    setFilterStatus('')
    setFilterType('All')
    setFilterGameId('')
    setFilterDeviceId('')
    setFilterTicketId('')
  }

  // Play method parsing functions (copied from TicketDetail.js)
  const parseKenoPlayMethod = (method) => {
    switch (method) {
      case 'LEVEL_1':
        return 'Bậc 1'
      case 'LEVEL_2':
        return 'Bậc 2'
      case 'LEVEL_3':
        return 'Bậc 3'
      case 'LEVEL_4':
        return 'Bậc 4'
      case 'LEVEL_5':
        return 'Bậc 5'
      case 'LEVEL_6':
        return 'Bậc 6'
      case 'LEVEL_7':
        return 'Bậc 7'
      case 'LEVEL_8':
        return 'Bậc 8'
      case 'LEVEL_9':
        return 'Bậc 9'
      case 'LEVEL_10':
        return 'Bậc 10'
      case 'UPPER':
        return 'Lớn'
      case 'LOWER':
        return 'Nhỏ'
      case 'EVEN':
        return 'Chẵn'
      case 'ODD':
        return 'Lẻ'
      case 'DRAW_UPPER_LOWER':
        return 'Hòa lớn nhỏ'
      case 'DRAW_ODD_EVEN':
        return 'Hòa chẵn lẻ'
      case 'EVEN_1112':
        return 'Chẵn 11-12'
      case 'ODD_1112':
        return 'Lẻ 11-12'
      default:
        return method || '';
    }
  }

  const parseMegaPlayMethod = (method) => {
    switch (method) {
      case 'GROUP_18':
        return 'Bao 18'
      case 'GROUP_15':
        return 'Bao 15'
      case 'GROUP_14':
        return 'Bao 14'
      case 'GROUP_13':
        return 'Bao 13'
      case 'GROUP_12':
        return 'Bao 12'
      case 'GROUP_11':
        return 'Bao 11'
      case 'GROUP_10':
        return 'Bao 10'
      case 'GROUP_9':
        return 'Bao 9'
      case 'GROUP_8':
        return 'Bao 8'
      case 'GROUP_7':
        return 'Bao 7'
      case 'GROUP_5':
        return 'Bao 5'
      case 'GROUP_6':
        return 'Vé thường'
      default:
        return method || '';
    }
  }

  const parseMax3dProPlayMethod = (method, numbers = 0) => {
    switch (method) {
      case 'MULTIGROUP':
        return `Bao ${numbers} bộ số`
      case 'GROUP':
        return `Bao bộ số`
      default:
        return method || 'BÌNH THƯỜNG';
    }
  }

  const parsePlayMethod = (gameId, method, numbers = 0) => {
    if (!method) return '-';
    
    switch (gameId?.toLowerCase()) {
      case 'keno':
        return parseKenoPlayMethod(method);
      case 'mega':
        return parseMegaPlayMethod(method);
      case 'power':
        return parseMegaPlayMethod(method);
      case 'max3d':
        return method;
      case 'max3dpro':
        return parseMax3dProPlayMethod(method, numbers);
      default:
        return method;
    }
  }

  return (
    <Container fluid className="print-queue-monitor py-3 px-0">
      <Row className="mb-4 g-0">
        <Col>
          <Card className="border-0 shadow-sm">
            <Card.Header className="print-queue-header d-flex justify-content-between align-items-center flex-wrap gap-3">
              <div className="d-flex align-items-center gap-3">
                <div className="print-queue-title-icon" aria-hidden="true">
                  <FontAwesomeIcon icon={faPrint} />
                </div>
                <div>
                  <h4 className="mb-1">Hàng đợi in</h4>
                  <div className="print-queue-live-status">
                    <span className={`live-dot ${autoRefresh ? 'is-live' : ''}`} />
                    {autoRefresh ? 'Đang tự động cập nhật mỗi 5 giây' : 'Đã tạm dừng tự động cập nhật'}
                  {lastUpdate && (
                    <span className="ms-2">
                      · Cập nhật lúc {moment(lastUpdate).format('HH:mm:ss')}
                    </span>
                  )}
                  </div>
                </div>
              </div>
              <div className="print-queue-toolbar d-flex gap-2">
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleCleanup}
                  disabled={cleaning}
                  title="Đồng bộ các bản ghi đã hoàn thành còn sót trong hàng đợi"
                >
                  {cleaning
                    ? <Spinner animation="border" size="sm" className="me-1" />
                    : <FontAwesomeIcon icon={faBroom} className="me-1" />}
                  {cleaning ? 'Đang quét...' : 'Quét dọn rác'}
                </Button>
                {selectedItems.size > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={openBulkAssignModal}
                  >
                    <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                    Gán hàng loạt ({selectedItems.size})
                  </Button>
                )}
                <Button
                  variant={autoRefresh ? "outline-secondary" : "primary"}
                  size="sm"
                  onClick={toggleAutoRefresh}
                >
                  <FontAwesomeIcon icon={autoRefresh ? faPause : faPlay} className="me-1" />
                  {autoRefresh ? 'Tạm dừng' : 'Tiếp tục cập nhật'}
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleManualRefresh}
                  disabled={loading}
                >
                  <FontAwesomeIcon icon={faSync} className={`me-1 ${loading ? 'fa-spin' : ''}`} />
                  Làm mới
                </Button>
              </div>
            </Card.Header>
            <Card.Body>
              {/* Statistics */}
              <Row className="print-queue-stats mb-3 g-3">
                <Col sm={6} xl={3}>
                  <div className="print-queue-stat is-total">
                    <span className="stat-label">Tổng trong hàng đợi</span>
                    <strong>{stats.total}</strong>
                    <span className="stat-hint">vé phù hợp bộ lọc</span>
                  </div>
                </Col>
                <Col sm={6} xl={3}>
                  <div className="print-queue-stat is-vietlott">
                    <span className="stat-label">Vietlott</span>
                    <strong>{stats.totalVietlott}</strong>
                    <span className="stat-hint">vé điện toán</span>
                  </div>
                </Col>
                <Col sm={6} xl={3}>
                  <div className="print-queue-stat is-classic">
                    <span className="stat-label">Classic</span>
                    <strong>{stats.totalClassic}</strong>
                    <span className="stat-hint">vé truyền thống</span>
                  </div>
                </Col>
                <Col sm={6} xl={3}>
                  <div className="print-queue-stat is-loto">
                    <span className="stat-label">Loto</span>
                    <strong>{stats.totalLoto}</strong>
                    <span className="stat-hint">vé loto</span>
                  </div>
                </Col>
              </Row>

              {/* Filters */}
              <div className="print-queue-filter mb-3">
                <div className="filter-heading d-flex justify-content-between align-items-center mb-3">
                  <div>
                    <FontAwesomeIcon icon={faSearch} className="me-2" />
                    <strong>Lọc nhanh</strong>
                    {activeFilterCount > 0 && <Badge bg="primary" className="ms-2">{activeFilterCount}</Badge>}
                  </div>
                  {activeFilterCount > 0 && (
                    <Button variant="link" size="sm" className="clear-filter-btn" onClick={clearFilters}>
                      <FontAwesomeIcon icon={faTimes} className="me-1" /> Xóa bộ lọc
                    </Button>
                  )}
                </div>
              <Row className="g-3 align-items-end">
                <Col md={2}>
                  <label className="form-label small">Trạng thái</label>
                  <select className="form-select" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                    <option value="">Tất cả</option>
                    <option value="0">Chờ in</option>
                    <option value="1">Đang in</option>
                    <option value="2">Đã in</option>
                    <option value="3">Hoàn thành</option>
                    <option value="-1">Đã hủy</option>
                  </select>
                </Col>
                <Col md={2}>
                  <label className="form-label small">Loại</label>
                  <select className="form-select" value={filterType} onChange={(e) => setFilterType(e.target.value)}>
                    <option value="All">Tất cả</option>
                    <option value="Vietlott">Vietlott</option>
                    <option value="Classic">Classic</option>
                    <option value="Loto">Loto</option>
                  </select>
                </Col>
                <Col md={2}>
                  <label className="form-label small">Game</label>
                  <select 
                    className="form-select" 
                    value={filterGameId} 
                    onChange={(e) => setFilterGameId(e.target.value)}
                    disabled={gamesLoading}
                  >
                    {gamesLoading ? (
                      <option value="">Đang tải...</option>
                    ) : (
                      games.map(game => (
                        <option key={game.id || 'all'} value={game.id || ''}>
                          {game.name}
                        </option>
                      ))
                    )}
                  </select>
                </Col>
                <Col md={2}>
                  <label className="form-label small">Thiết bị</label>
                  <select 
                    className="form-select" 
                    value={filterDeviceId} 
                    onChange={(e) => setFilterDeviceId(e.target.value)}
                    disabled={devicesLoading}
                  >
                    {devicesLoading ? (
                      <option value="">Đang tải...</option>
                    ) : (
                      devices.map(device => (
                        <option key={device.id || 'all'} value={device.id || ''}>
                          {device.code}
                        </option>
                      ))
                    )}
                  </select>
                </Col>
                <Col md={4}>
                  <label className="form-label small">Ticket ID</label>
                  <div className="input-group">
                    <input 
                      className="form-control" 
                      value={filterTicketId} 
                      onChange={(e) => setFilterTicketId(e.target.value)} 
                      placeholder="Nhập mã vé cần tìm..."
                    />
                    {filterTicketId && (
                      <Button 
                        variant="outline-secondary" 
                        size="sm"
                        onClick={() => setFilterTicketId('')}
                      >
                        Xóa
                      </Button>
                    )}
                  </div>
                </Col>
              </Row>
              </div>
              {error && (
                <Alert variant="danger" className="mb-3">
                  {error}
                </Alert>
              )}
              {cleanupMessage && (
                <Alert variant="success" dismissible onClose={() => setCleanupMessage('')} className="mb-3">
                  {cleanupMessage}
                </Alert>
              )}
              {cleanupError && (
                <Alert variant="danger" dismissible onClose={() => setCleanupError('')} className="mb-3">
                  {cleanupError}
                </Alert>
              )}

              {loading && (
                <div className="text-center py-3">
                  <Spinner animation="border" role="status">
                    <span className="visually-hidden">Đang tải...</span>
                  </Spinner>
                </div>
              )}

              {/* Table */}
              <div className="queue-list-heading d-flex justify-content-between align-items-end mb-2">
                <div>
                  <h6 className="mb-1">Danh sách công việc</h6>
                  <span className="small text-muted">Chọn vé để gán nhân viên hàng loạt</span>
                </div>
                <span className="small text-muted">{totalItems} kết quả</span>
              </div>
              <div className="table-responsive print-queue-table-wrap">
                <Table hover className="mb-0 print-queue-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}>
                        <input
                          type="checkbox"
                          checked={data.length > 0 && selectedItems.size === data.length}
                          onChange={handleSelectAll}
                          style={{ cursor: 'pointer' }}
                        />
                      </th>
                      <th className="action-column">Thao tác</th>
                      <th>Ticket ID</th>
                      <th>Game</th>
                      <th>Kỳ</th>
                      <th>Thiết bị</th>
                      <th>Trạng thái</th>
                      <th>Thời gian tạo</th>
                      <th>Cập nhật lúc</th>
                      <th>In xong lúc</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 && !loading ? (
                      <tr>
                        <td colSpan="10" className="text-center py-4">
                          <em className="text-muted">Không có dữ liệu</em>
                        </td>
                      </tr>
                    ) : (
                      data.map((item, index) => {
                        const isDisabled = item.status === 3 || item.status === -1
                        const rowStyle = getRowStyle(item.gameId)
                        return (
                          <tr key={`${item.type}-${item.id}`} style={{ ...rowStyle, opacity: isDisabled ? 0.6 : 1 }}>
                            <td>
                              <input
                                type="checkbox"
                                checked={selectedItems.has(getItemKey(item))}
                                onChange={() => handleCheckboxChange(item)}
                                disabled={isDisabled}
                                style={{ cursor: isDisabled ? 'not-allowed' : 'pointer' }}
                              />
                            </td>
                            <td>
                              <Button variant="outline-secondary" size="sm" title="Xem chi tiết" onClick={() => handleShowDetail(item)}>
                                <FontAwesomeIcon icon={faEye} />
                              </Button>
                              <Button variant="outline-secondary" size="sm" className="ms-1" title="Gán tay" onClick={() => openAssignModal(item)}>
                                <FontAwesomeIcon icon={faUserPlus} />
                              </Button>
                              <Button variant="danger" size="sm" className="ms-1" title="Hủy đơn" onClick={() => handleCancelCart(item)} disabled={cancelling === item.id || isDisabled}>
                                {cancelling === item.id ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faTrash} />}
                              </Button>
                            </td>
                            <td>{renderTicketIdLink(item)}</td>
                            <td>{getGameLogo(item.gameId) || item.gameId || '-'}</td>
                            <td>{getPeriodName(item.gamePeriodId) || <span className="text-muted">-</span>}</td>
                            <td>{item.deviceId || (item.employeeId ? `NV #${item.employeeId}` : '-')}</td>
                            <td>{getStatusBadge(item.status)}</td>
                            <td>{formatDateTime(item.createdAt)}</td>
                            <td>{formatDateTime(item.modified)}</td>
                            <td>{formatDateTime(item.printedAt)}</td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </Table>
              </div>

              {/* Pagination */}
              {totalItems > 0 && (
                <Row className="mt-3">
                  <Col className="d-flex justify-content-between align-items-center">
                    <div className="d-flex align-items-center gap-2">
                      <div className="text-muted small">
                        Hiển thị trang {currentPage + 1} / {Math.max(1, totalPages)} (Tổng {totalItems} vé)
                      </div>
                      {selectedItems.size > 0 && (
                        <Button
                          variant="warning"
                          size="sm"
                          onClick={openBulkAssignModal}
                        >
                          <FontAwesomeIcon icon={faUserPlus} className="me-1" />
                          Gán hàng loạt ({selectedItems.size})
                        </Button>
                      )}
                    </div>
                    {totalPages > 1 && (
                    <div className="d-flex gap-1">
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handlePageChange(0)}
                        disabled={currentPage === 0}
                      >
                        ❮❮
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 0}
                      >
                        ❮ Trước
                      </Button>
                      
                      {/* Page numbers */}
                      {(() => {
                        const pages = [];
                        const maxPagesToShow = 5;
                        let startPage = Math.max(0, currentPage - Math.floor(maxPagesToShow / 2));
                        let endPage = Math.min(totalPages - 1, startPage + maxPagesToShow - 1);
                        
                        // Adjust start if we're near the end
                        if (endPage - startPage < maxPagesToShow - 1) {
                          startPage = Math.max(0, endPage - maxPagesToShow + 1);
                        }

                        for (let i = startPage; i <= endPage; i++) {
                          pages.push(
                            <Button
                              key={i}
                              variant={i === currentPage ? "primary" : "outline-primary"}
                              size="sm"
                              onClick={() => handlePageChange(i)}
                            >
                              {i + 1}
                            </Button>
                          );
                        }
                        return pages;
                      })()}

                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage >= totalPages - 1}
                      >
                        Sau ❯
                      </Button>
                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() => handlePageChange(totalPages - 1)}
                        disabled={currentPage >= totalPages - 1}
                      >
                        ❯❯
                      </Button>
                    </div>
                    )}
                  </Col>
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Detail Modal */}
      <Modal show={showDetailModal} onHide={handleCloseDetail} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontWeight: 'normal', fontSize: 15 }}>
            {selectedItem && (
              <>
                Mã vé: {selectedItem.id}
                {selectedItem.gameId && ` ${selectedItem.gameId}`}
              </>
            )}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedItem && (
            <div>
              {/* Status Header */}
              <h5 className="mb-4">
                {selectedItem.status === -1 ? 'ĐÃ HỦY' :
                 selectedItem.status === 0 ? 'CHỜ IN' :
                 selectedItem.status === 1 ? 'ĐANG IN' :
                 selectedItem.status === 2 ? 'ĐÃ IN THÀNH CÔNG' :
                 selectedItem.status === 3 ? 'ĐÃ HOÀN THÀNH' :
                 'ĐANG CHỜ XỬ LÝ'}
              </h5>

              {/* Basic Information */}
              <Row className="mb-3">
                <Col md={6}>
                  <strong>Loại:</strong> {getTypeBadge(selectedItem.type)}
                </Col>
                <Col md={6}>
                  <strong>Game:</strong>
                  <div className="mt-1">
                    {getGameLogo(selectedItem.gameId) || selectedItem.gameId || '-'}
                  </div>
                </Col>
              </Row>

              <Row className="mb-3">
                <Col md={6}>
                  <strong>Device ID:</strong> {selectedItem.deviceId || '-'}
                </Col>
                <Col md={6}>
                  <strong>Ưu tiên:</strong>
                  <Badge bg={selectedItem.priority > 0 ? "danger" : "secondary"} className="ms-2">
                    {selectedItem.priority}
                  </Badge>
                </Col>
              </Row>

              {selectedItem.gamePeriodId && (
                <Row className="mb-3">
                  <Col md={12}>
                    <strong>Kỳ in:</strong> {getPeriodName(selectedItem.gamePeriodId) || '-'}
                  </Col>
                </Row>
              )}

              {/* Ticket Details Table */}
              {selectedItem.detail && (() => {
                try {
                  const ticketDetail = JSON.parse(selectedItem.detail);
                  if (ticketDetail.numberRows && Array.isArray(ticketDetail.numberRows)) {
                    return (
                      <>
                        <h6 className="mt-4 mb-3">Thông tin vé</h6>
                        <Table responsive className="align-items-center table-flush">
                          <thead className="thead-light">
                            <tr>
                              <th scope="col">STT</th>
                              <th scope="col">Dãy số</th>
                              <th scope="col">Cách chơi</th>
                              <th scope="col">Giá trị</th>
                            </tr>
                          </thead>
                          <tbody>
                            {ticketDetail.numberRows.map((row, index) => (
                              <tr key={`row-${index}`}>
                                <td>{index + 1}</td>
                                <td>{row.numbers || '-'}</td>
                                <td>{parsePlayMethod(selectedItem.gameId, row.playMethod, row.numbers?.split(' ').length)}</td>
                                <td className="text-end">
                                  {row.value ? new Intl.NumberFormat('vi-VN').format(row.value) : (row.unitValue ? new Intl.NumberFormat('vi-VN').format(row.unitValue) : '-')}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </>
                    );
                  }
                } catch (e) {
                  // If parsing fails, continue to show basic details
                }
                return null;
              })()}

              {/* Value and Customer Info */}
              {selectedItem.detail && (() => {
                try {
                  const ticketDetail = typeof selectedItem.detail === 'string' ? JSON.parse(selectedItem.detail) : selectedItem.detail
                  const value = ticketDetail.value
                  const customer = ticketDetail.customer || ticketDetail.cart?.user
                  
                  if (value || customer) {
                    return (
                      <Row className="mb-3 mt-3">
                        {value && (
                          <Col md={6}>
                            <div className="p-3 rounded" style={{ backgroundColor: '#ecfdf5', border: '1px solid #10B981' }}>
                              <small className="text-muted">Giá trị vé:</small>
                              <div className="fw-bold fs-5" style={{ color: '#059669' }}>
                                {new Intl.NumberFormat('vi-VN').format(value)} đ
                              </div>
                            </div>
                          </Col>
                        )}
                        {customer && (
                          <Col md={6}>
                            <div className="p-3 rounded" style={{ backgroundColor: '#f0f9ff', border: '1px solid #3B82F6' }}>
                              <small className="text-muted">Khách hàng:</small>
                              <div className="fw-bold">{customer.fullname || customer.username || '-'}</div>
                              {customer.phone && <small className="text-muted">SĐT: {customer.phone}</small>}
                            </div>
                          </Col>
                        )}
                      </Row>
                    )
                  }
                } catch (e) {}
                return null
              })()}

              {/* Images Section */}
              {selectedItem.images && (
                <>
                  <Row className="mt-4">
                    <Col>
                      <h6>Hình ảnh vé</h6>
                    </Col>
                  </Row>
                  <Row>
                    {(() => {
                      try {
                        const images = typeof selectedItem.images === 'string' 
                          ? JSON.parse(selectedItem.images) 
                          : selectedItem.images;
                        
                        if (Array.isArray(images)) {
                          return images.map((image, index) => (
                            <Col key={index} md={6}>
                              <img
                                alt={`Vé ${index + 1}`}
                                height={200}
                                src={image}
                                style={{ cursor: 'pointer', maxWidth: '100%' }}
                                className="mb-2"
                                onClick={() => {
                                  // You can implement image preview here if needed
                                  window.open(image, '_blank');
                                }}
                              />
                            </Col>
                          ));
                        }
                      } catch (e) {
                        return (
                          <Col>
                            <span className="text-muted">Không thể hiển thị hình ảnh</span>
                          </Col>
                        );
                      }
                    })()}
                  </Row>
                </>
              )}

              {/* Technical Details - Collapsible */}
              <Row className="mt-4">
                <Col>
                  <details>
                    <summary style={{ cursor: 'pointer' }}>
                      <strong>Thông tin kỹ thuật</strong>
                    </summary>
                    <div className="mt-2">
                      <Row className="mb-2">
                        <Col md={6}>
                          <small><strong>Tạo lúc:</strong><br />{formatDateTime(selectedItem.createdAt)}</small>
                        </Col>
                        <Col md={6}>
                          <small><strong>Cập nhật:</strong><br />{formatDateTime(selectedItem.modified)}</small>
                        </Col>
                      </Row>

                      <Row className="mb-2">
                        <Col md={6}>
                          <small><strong>In lúc:</strong><br />{formatDateTime(selectedItem.printedAt)}</small>
                        </Col>
                        <Col md={6}>
                          <small><strong>Roll lúc:</strong><br />{formatDateTime(selectedItem.rolledAt)}</small>
                        </Col>
                      </Row>

                      {selectedItem.region && (
                        <Row className="mb-2">
                          <Col md={6}>
                            <small><strong>Region:</strong> {selectedItem.region}</small>
                          </Col>
                          <Col md={6}>
                            <small><strong>Employee ID:</strong> {selectedItem.employeeId || '-'}</small>
                          </Col>
                        </Row>
                      )}

                      {selectedItem.userType !== undefined && (
                        <Row className="mb-2">
                          <Col md={12}>
                            <small><strong>User Type:</strong> {selectedItem.userType}</small>
                          </Col>
                        </Row>
                      )}

                      {selectedItem.command && (
                        <Row className="mb-2">
                          <Col md={12}>
                            <small><strong>Command:</strong></small>
                            <pre className="bg-light p-2 mt-1 small" style={{fontSize: '11px'}}>{selectedItem.command}</pre>
                          </Col>
                        </Row>
                      )}

                      {selectedItem.detail && (
                        <Row className="mb-2">
                          <Col md={12}>
                            <small><strong>Raw Detail:</strong></small>
                            <pre className="bg-light p-2 mt-1 small" style={{maxHeight: '150px', overflow: 'auto', fontSize: '11px'}}>
                              {(() => {
                                try {
                                  return JSON.stringify(JSON.parse(selectedItem.detail), null, 2)
                                } catch {
                                  return selectedItem.detail
                                }
                              })()}
                            </pre>
                          </Col>
                        </Row>
                      )}
                    </div>
                  </details>
                </Col>
              </Row>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDetail}>
            Đóng
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Assign Modal */}
      <Modal show={showAssignModal} onHide={closeAssignModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Gán tay: {assignTarget ? `${assignTarget.type} - ${assignTarget.id}` : ''}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {assignError && (
            <Alert variant="danger" className="mb-3">{assignError}</Alert>
          )}
          {assignSuccess && (
            <Alert variant="success" className="mb-3">{assignSuccess}</Alert>
          )}

          <Row className="mb-3">
            <Col>
              <small className="text-muted">
                {assignTarget?.type === 'Vietlott' 
                  ? 'Chọn nhân viên có ca đang hoạt động và đã kết nối thiết bị' 
                  : 'Chọn nhân viên để gán vé'}
              </small>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col md={8}>
              <input
                className="form-control"
                placeholder="Tìm kiếm nhân viên (tên đăng nhập/tên)"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
              />
            </Col>
            <Col md={4} className="text-end">
              <Button variant="outline-secondary" size="sm" onClick={() => loadEmployees(empSearch)} disabled={empLoading}>
                {empLoading ? 'Đang tải...' : 'Làm mới'}
              </Button>
            </Col>
          </Row>

          <div style={{ maxHeight: 300, overflow: 'auto' }}>
            {empLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Tên</th>
                    <th>Thiết bị</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length === 0 ? (
                    <tr><td colSpan="4" className="text-center text-muted">Không có nhân viên</td></tr>
                  ) : employees.map(emp => (
                    <tr key={emp.id} className={selectedEmployee?.id === emp.id ? 'table-active' : ''}>
                      <td>{emp.username}</td>
                      <td>{emp.name || '-'}</td>
                      <td>
                        {emp.currentDevice ? (
                          <Badge bg="info">{emp.currentDevice.code}</Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-end">
                        <Button 
                          size="sm" 
                          variant={selectedEmployee?.id === emp.id ? 'success' : 'outline-primary'}
                          onClick={() => setSelectedEmployee(emp)}
                        >
                          {selectedEmployee?.id === emp.id ? 'Đã chọn' : 'Chọn'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeAssignModal}>Hủy</Button>
          <Button variant="primary" onClick={handleAssign} disabled={assigning || !selectedEmployee}>
            {assigning ? <Spinner animation="border" size="sm" /> : 'Xác nhận gán'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Bulk Assign Modal */}
      <Modal show={showBulkAssignModal} onHide={closeBulkAssignModal} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>
            Gán hàng loạt - {selectedItems.size} vé đã chọn
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {bulkAssignError && (
            <Alert variant="danger" className="mb-3">{bulkAssignError}</Alert>
          )}
          {bulkAssignSuccess && (
            <Alert variant="success" className="mb-3">{bulkAssignSuccess}</Alert>
          )}

          {/* Selected items list */}
          <Row className="mb-3">
            <Col>
              <h6>Danh sách vé đã chọn:</h6>
              <div style={{ maxHeight: 150, overflow: 'auto', backgroundColor: '#f8f9fa', padding: '10px', borderRadius: '5px' }}>
                {getSelectedItemsData().map((item, index) => (
                  <div key={getItemKey(item)} className="mb-1">
                    <small>
                      {index + 1}. {getTypeBadge(item.type)} - <code>{item.id}</code>
                      {item.gameId && ` (${item.gameId})`}
                    </small>
                  </div>
                ))}
              </div>
            </Col>
          </Row>

          <Row className="mb-3">
            <Col>
              <Alert variant="info" className="mb-0">
                <small>Tất cả vé sẽ được gán cho nhân viên đã chọn</small>
              </Alert>
            </Col>
          </Row>

          <Row className="mb-2">
            <Col md={8}>
              <input
                className="form-control"
                placeholder="Tìm kiếm nhân viên (tên đăng nhập/tên)"
                value={empSearch}
                onChange={(e) => setEmpSearch(e.target.value)}
              />
            </Col>
            <Col md={4} className="text-end">
              <Button variant="outline-secondary" size="sm" onClick={() => loadEmployees(empSearch)} disabled={empLoading}>
                {empLoading ? 'Đang tải...' : 'Làm mới'}
              </Button>
            </Col>
          </Row>

          <div style={{ maxHeight: 300, overflow: 'auto' }}>
            {empLoading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <Table hover size="sm">
                <thead>
                  <tr>
                    <th>Username</th>
                    <th>Tên</th>
                    <th>Thiết bị</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {employees.length === 0 ? (
                    <tr><td colSpan="4" className="text-center text-muted">Không có nhân viên</td></tr>
                  ) : employees.map(emp => (
                    <tr key={emp.id} className={selectedEmployee?.id === emp.id ? 'table-active' : ''}>
                      <td>{emp.username}</td>
                      <td>{emp.name || '-'}</td>
                      <td>
                        {emp.currentDevice ? (
                          <Badge bg="info">{emp.currentDevice.code}</Badge>
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td className="text-end">
                        <Button 
                          size="sm" 
                          variant={selectedEmployee?.id === emp.id ? 'success' : 'outline-primary'}
                          onClick={() => setSelectedEmployee(emp)}
                        >
                          {selectedEmployee?.id === emp.id ? 'Đã chọn' : 'Chọn'}
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>

          {/* Progress indicator */}
          {bulkAssigning && bulkAssignProgress.total > 0 && (
            <Row className="mt-3">
              <Col>
                <div className="text-center">
                  <small className="text-muted">
                    Đang xử lý: {bulkAssignProgress.current} / {bulkAssignProgress.total}
                  </small>
                  <div className="progress mt-2" style={{ height: '5px' }}>
                    <div 
                      className="progress-bar" 
                      role="progressbar" 
                      style={{ width: `${(bulkAssignProgress.current / bulkAssignProgress.total) * 100}%` }}
                    ></div>
                  </div>
                </div>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeBulkAssignModal} disabled={bulkAssigning}>
            Hủy
          </Button>
          <Button 
            variant="warning" 
            onClick={handleBulkAssign} 
            disabled={bulkAssigning || !selectedEmployee || selectedItems.size === 0}
          >
            {bulkAssigning ? (
              <>
                <Spinner animation="border" size="sm" className="me-2" />
                Đang gán...
              </>
            ) : (
              `Xác nhận gán ${selectedItems.size} vé`
            )}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}

export default PrintQueueMonitor
